import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Mail, Check, Shield, User, Phone, Calendar, Globe, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = 'https://admin.afriperx.online/api';
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

const COUNTRY_CODES = [
  { code: "+27", country: "South Africa", flag: "ZA" },
  { code: "+254", country: "Kenya", flag: "KE" },
  { code: "+234", country: "Nigeria", flag: "NG" },
  { code: "+233", country: "Ghana", flag: "GH" },
  { code: "+256", country: "Uganda", flag: "UG" },
  { code: "+255", country: "Tanzania", flag: "TZ" },
  { code: "+263", country: "Zimbabwe", flag: "ZW" },
  { code: "+260", country: "Zambia", flag: "ZM" },
  { code: "+267", country: "Botswana", flag: "BW" },
  { code: "+258", country: "Mozambique", flag: "MZ" },
];

const SERVICES = [
  { id: "rewards", label: "Partner Rewards & Discounts", desc: "Earn cashback at 25+ stores" },
  { id: "banking", label: "Digital Banking (Diamond Card)", desc: "Payments, transfers & more" },
  { id: "insurance", label: "Insurance Cover", desc: "Funeral, life, medical & vehicle" },
  { id: "loans", label: "Quick Loans", desc: "Same-day payday loans" },
  { id: "travel", label: "Travel Deals", desc: "Flights & accommodation" },
  { id: "savings", label: "Savings & Investments", desc: "Grow your wealth" },
];

export const GetStartedPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: '', surname: '', age: '', email: '',
    countryCode: '+27', mobile: '', acceptTerms: false,
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleService = (id) => {
    setSelectedServices(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const validateStep1 = () => {
    if (!form.name.trim()) { toast.error('Please enter your name'); return false; }
    if (!form.surname.trim()) { toast.error('Please enter your surname'); return false; }
    if (!form.age || parseInt(form.age) < 18 || parseInt(form.age) > 120) { toast.error('Please enter a valid age (18+)'); return false; }
    if (!form.email.trim() || !form.email.includes('@')) { toast.error('Please enter a valid email address'); return false; }
    if (!form.mobile.trim() || form.mobile.length < 7) { toast.error('Please enter a valid mobile number'); return false; }
    if (!form.acceptTerms) { toast.error('Please accept the Terms & Conditions'); return false; }
    return true;
  };

  const [emailSent, setEmailSent] = useState(true);
  const [fallbackCode, setFallbackCode] = useState('');

  const handleSendVerification = async () => {
    if (!validateStep1()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register/send-otp`, {
        name: form.name,
        surname: form.surname,
        age: form.age,
        email: form.email,
        mobile: form.mobile,
        countryCode: form.countryCode,
      });
      setEmailSent(res.data.email_sent);
      if (!res.data.email_sent && res.data.otp_fallback) {
        setFallbackCode(res.data.otp_fallback);
      }
      setStep(2);
      toast.success(res.data.email_sent
        ? `Verification code sent to ${form.email}`
        : 'Email could not be sent. Use the fallback code shown below.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send verification code';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const [loginCredentials, setLoginCredentials] = useState(null);

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) { toast.error('Please enter the verification code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register/verify-otp`, {
        email: form.email,
        otp: verificationCode,
        services: [],
      });
      if (res.data.login_mobile) {
        setLoginCredentials({
          mobile: res.data.login_mobile,
          passcode: res.data.login_passcode,
        });
      }
      setStep(3);
      toast.success('Email verified successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid verification code';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const [showSuccess, setShowSuccess] = useState(false);

  const handleFinish = async () => {
    setLoading(true);
    try {
      // Update services for the registered user
      if (selectedServices.length > 0) {
        await axios.post(`${API}/auth/register/update-services`, {
          email: form.email,
          services: selectedServices,
        });
      }
    } catch (e) {
      // Non-critical, continue anyway
    }
    setLoading(false);
    setShowSuccess(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-amber-50/30" data-testid="get-started-page">
      {/* Simple Top Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="AfriPerx" className="h-10 w-auto" />
          </Link>
          <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors">
            Back to Home
          </button>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-lg mx-auto">
          {/* Progress */}
          {!showSuccess && (
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="progress-steps">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  step >= s ? 'bg-afriperx-green text-white' : 'bg-gray-200 text-gray-400'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && <div className={`w-12 h-0.5 transition-all duration-300 ${step > s ? 'bg-afriperx-green' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
          )}

          {/* Step 1: Registration Form */}
          {step === 1 && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in" data-testid="step-1-form">
              <div className="text-center mb-6">
                <h1 className="font-syne text-2xl font-bold text-gray-900 mb-2">Create Your Account</h1>
                <p className="text-gray-500 text-sm">Join 208 000+ members earning rewards daily</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      <User className="w-3 h-3 inline mr-1" />Name *
                    </label>
                    <input
                      type="text" value={form.name} onChange={e => updateForm('name', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm"
                      placeholder="First name" data-testid="gs-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      <User className="w-3 h-3 inline mr-1" />Surname *
                    </label>
                    <input
                      type="text" value={form.surname} onChange={e => updateForm('surname', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm"
                      placeholder="Last name" data-testid="gs-surname"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      <Calendar className="w-3 h-3 inline mr-1" />Age *
                    </label>
                    <input
                      type="number" value={form.age} onChange={e => updateForm('age', e.target.value)}
                      min="18" max="120"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm"
                      placeholder="Your age" data-testid="gs-age"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      <Mail className="w-3 h-3 inline mr-1" />Email *
                    </label>
                    <input
                      type="email" value={form.email} onChange={e => updateForm('email', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm"
                      placeholder="your@email.com" data-testid="gs-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    <Phone className="w-3 h-3 inline mr-1" />Mobile Number *
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={form.countryCode} onChange={e => updateForm('countryCode', e.target.value)}
                      className="w-28 px-3 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green outline-none text-sm bg-white"
                      data-testid="gs-country-code"
                    >
                      {COUNTRY_CODES.map(cc => (
                        <option key={cc.code} value={cc.code}>{cc.code} ({cc.country})</option>
                      ))}
                    </select>
                    <input
                      type="tel" value={form.mobile} onChange={e => updateForm('mobile', e.target.value)}
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm"
                      placeholder="Phone number" data-testid="gs-mobile"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 cursor-pointer hover:border-afriperx-green/30 transition-colors" data-testid="gs-terms-label">
                  <input
                    type="checkbox" checked={form.acceptTerms} onChange={e => updateForm('acceptTerms', e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-afriperx-green focus:ring-afriperx-green"
                    data-testid="gs-terms-checkbox"
                  />
                  <span className="text-xs text-gray-500 leading-relaxed">
                    I agree to the <Link to="/terms" className="text-afriperx-green font-medium hover:underline">Terms & Conditions</Link> and <span className="text-afriperx-green font-medium">Privacy Policy</span>. I consent to AfriPerx processing my personal information.
                  </span>
                </label>

                <button
                  onClick={handleSendVerification}
                  disabled={loading}
                  className="w-full py-3.5 bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="gs-verify-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Verify Email & Continue</>}
                </button>
              </div>

              <p className="text-center text-xs text-gray-400 mt-4">
                Already have an account? <button onClick={() => navigate('/login')} className="text-afriperx-green font-medium hover:underline">Sign In</button>
              </p>
            </div>
          )}

          {/* Step 2: Email Verification */}
          {step === 2 && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in" data-testid="step-2-verify">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-afriperx-green/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-afriperx-green" />
                </div>
                <h1 className="font-syne text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
                <p className="text-gray-500 text-sm">We sent a 6-digit code to <span className="font-medium text-gray-700">{form.email}</span></p>
              </div>

              {/* Fallback code display - only when email failed */}
              {!emailSent && fallbackCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-center" data-testid="fallback-code-display">
                  <p className="text-xs text-amber-600 font-medium mb-1">Email could not be sent. Use this code:</p>
                  <p className="text-2xl font-mono font-bold text-amber-800 tracking-[0.3em]">{fallbackCode}</p>
                </div>
              )}

              {emailSent && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-center" data-testid="email-sent-notice">
                  <p className="text-xs text-green-700 font-medium">Check your inbox (and spam folder) for the verification code from AfriPerx.</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Enter 6-digit code</label>
                  <input
                    type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="w-full px-4 py-4 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-center text-2xl font-mono tracking-[0.3em]"
                    placeholder="000000" data-testid="gs-otp-input"
                  />
                </div>

                <button
                  onClick={handleVerifyCode}
                  disabled={loading || verificationCode.length < 6}
                  className="w-full py-3.5 bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="gs-confirm-code-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Verify & Continue</>}
                </button>

                <button
                  onClick={() => setStep(1)}
                  className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors flex items-center justify-center gap-1"
                  data-testid="gs-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to form
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Select Services */}
          {step === 3 && !showSuccess && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in" data-testid="step-3-services">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-afriperx-gold/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-afriperx-gold" />
                </div>
                <h1 className="font-syne text-2xl font-bold text-gray-900 mb-2">Choose Your Services</h1>
                <p className="text-gray-500 text-sm">Select the services you're interested in (you can change this later)</p>
              </div>

              <div className="space-y-3 mb-6">
                {SERVICES.map(s => (
                  <label
                    key={s.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                      selectedServices.includes(s.id)
                        ? 'border-afriperx-green bg-afriperx-green/5 shadow-sm'
                        : 'border-gray-100 hover:border-afriperx-gold/30 bg-white'
                    }`}
                    data-testid={`service-${s.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedServices.includes(s.id)}
                      onChange={() => toggleService(s.id)}
                      className="w-4 h-4 rounded border-gray-300 text-afriperx-green focus:ring-afriperx-green"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{s.label}</p>
                      <p className="text-gray-400 text-xs">{s.desc}</p>
                    </div>
                    {selectedServices.includes(s.id) && <Check className="w-4 h-4 text-afriperx-green shrink-0" />}
                  </label>
                ))}
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleFinish}
                  disabled={loading}
                  className="w-full py-3.5 bg-afriperx-gold hover:bg-afriperx-gold-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="gs-finish-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> Complete Registration</>}
                </button>
                <p className="text-center text-xs text-gray-400">
                  You can always update your preferences from your dashboard
                </p>
              </div>
            </div>
          )}
          {/* Success Screen */}
          {showSuccess && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in text-center" data-testid="signup-success">
              <div className="w-20 h-20 rounded-full bg-afriperx-green/10 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-afriperx-green" />
              </div>
              <h1 className="font-syne text-2xl font-bold text-gray-900 mb-3">Welcome to AfriPerx!</h1>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
                Your account has been created successfully. Use the credentials below to sign in.
              </p>

              {loginCredentials && (
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-6 text-left" data-testid="login-credentials-box">
                  <p className="text-xs font-semibold text-afriperx-green uppercase tracking-wider mb-3">Your Login Details</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100">
                      <span className="text-xs text-gray-500">Mobile Number</span>
                      <span className="font-mono font-bold text-gray-900 text-sm" data-testid="cred-mobile">{loginCredentials.mobile}</span>
                    </div>
                    <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100">
                      <span className="text-xs text-gray-500">Passcode</span>
                      <span className="font-mono font-bold text-gray-900 text-sm" data-testid="cred-passcode">{loginCredentials.passcode}</span>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Please save these details — you'll need them to sign in.
                  </p>
                </div>
              )}

              <button
                onClick={() => navigate('/login')}
                className="w-full py-3.5 bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
                data-testid="gs-goto-login-btn"
              >
                <ArrowRight className="w-4 h-4" /> Sign In Now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
